import {
  VestingGraphData,
  VestingGraphPointStatus,
} from '../../../../../hooks/vesting-task/use-vesting-graph';
import { formatVestingCalendarDate } from './vesting-graph.format';
import { DateFormatter, VestingGraphSeriesPoint } from './vesting-graph.types';

const MIN_CLIFF_SLOT_WEIGHT = 2;

/** Empty cliff gutter width in equal-column units — never thinner than {@link MIN_CLIFF_SLOT_WEIGHT}. */
export const resolveCliffSlotWeight = (cliffMonths: number) =>
  Math.max(MIN_CLIFF_SLOT_WEIGHT, (cliffMonths / 6) * MIN_CLIFF_SLOT_WEIGHT);

/**
 * Equal-width payment columns. The grant-start anchor is an empty gutter: with a cliff it
 * stretches to at least {@link MIN_CLIFF_SLOT_WEIGHT} units (no bars inside); without a cliff
 * it stays one unit so Grant Start still has a place on the axis.
 */
export const applyEqualColumnLayout = (
  chartData: VestingGraphSeriesPoint[],
  { cliffMonths }: { cliffMonths?: number } = {},
) => {
  const hasCliff = cliffMonths != null && cliffMonths > 0;
  const startWeight = hasCliff ? resolveCliffSlotWeight(cliffMonths) : 1;

  let cursor = 0;
  const layoutChartData = chartData.map((point, index) => {
    const weight = index === 0 ? startWeight : 1;
    const slotHalfWidth = weight / 2;
    const timestamp = cursor + slotHalfWidth;
    cursor += weight;

    return {
      ...point,
      isCliffSpan: index === 0 && hasCliff,
      slotHalfWidth,
      timestamp,
    };
  });

  return {
    chartData: layoutChartData,
    domainMax: Math.max(cursor, 1),
    domainMin: 0,
  };
};

/**
 * Acceleration and expiry each own a dedicated column at their real calendar date —
 * even when a payment lands on the same day.
 */
export const insertScheduleEventColumns = ({
  chartData,
  format,
  vestingGraph,
}: {
  chartData: VestingGraphSeriesPoint[];
  format: DateFormatter;
  vestingGraph: VestingGraphData;
}): VestingGraphSeriesPoint[] => {
  let points = chartData;

  if (vestingGraph.accelerationDate) {
    points = insertDedicatedEventColumn({
      chartData: points,
      date: vestingGraph.accelerationDate,
      format,
      isAcceleration: true,
      isExpiry: false,
    });
  }

  if (vestingGraph.expiryDate) {
    points = insertDedicatedEventColumn({
      chartData: points,
      date: vestingGraph.expiryDate,
      format,
      isAcceleration: false,
      isExpiry: true,
    });
  }

  return points;
};

const buildScheduleEventPoint = ({
  date,
  format,
  isAcceleration,
  isExpiry,
  previous,
}: {
  date: string;
  format: DateFormatter;
  isAcceleration: boolean;
  isExpiry: boolean;
  previous: VestingGraphSeriesPoint;
}): VestingGraphSeriesPoint => {
  const calendarTimestamp = new Date(date).getTime();
  const status = isExpiry ? VestingGraphPointStatus.EXPIRED : VestingGraphPointStatus.UNVESTED;

  return {
    calendarTimestamp,
    cumulativePercent: previous.cumulativePercent,
    cumulativeShares: previous.cumulativeShares,
    dateLabel: formatVestingCalendarDate(format, date),
    expiredPercent: null,
    isAccelerationColumn: isAcceleration,
    isEventColumn: true,
    isExpiryColumn: isExpiry,
    monthsToComplete: previous.monthsToComplete,
    originalSchedulePercent: previous.originalSchedulePercent,
    periodShares: 0,
    projectedPercent: previous.cumulativePercent,
    slotHalfWidth: 0,
    status,
    timestamp: calendarTimestamp,
    vestedPercent: null,
    vestingStepShares: 0,
  };
};

/** Insert after every point on the same calendar day so a same-date payment keeps its bar. */
const insertDedicatedEventColumn = ({
  chartData,
  date,
  format,
  isAcceleration,
  isExpiry,
}: {
  chartData: VestingGraphSeriesPoint[];
  date: string;
  format: DateFormatter;
  isAcceleration: boolean;
  isExpiry: boolean;
}): VestingGraphSeriesPoint[] => {
  if (
    (isAcceleration && chartData.some((point) => point.isAccelerationColumn)) ||
    (isExpiry && chartData.some((point) => point.isExpiryColumn))
  ) {
    return chartData;
  }

  const calendarTimestamp = new Date(date).getTime();
  const insertAt = chartData.findIndex((point) => point.calendarTimestamp > calendarTimestamp);
  const index = insertAt < 0 ? chartData.length : insertAt;
  const previous = chartData[Math.max(index - 1, 0)]!;

  return [
    ...chartData.slice(0, index),
    buildScheduleEventPoint({ date, format, isAcceleration, isExpiry, previous }),
    ...chartData.slice(index),
  ];
};

/**
 * Remaps target columns (acceleration / expiry) onto a fixed pixel slot so the marker
 * stays a skinny 15px column regardless of how wide neighbouring payment bars are.
 */
export const applyFixedPixelColumnWidths = ({
  chartData,
  domainMax,
  domainMin,
  isTargetColumn,
  plotWidth,
  widthPx,
}: {
  chartData: VestingGraphSeriesPoint[];
  domainMax: number;
  domainMin: number;
  isTargetColumn: (point: VestingGraphSeriesPoint) => boolean;
  plotWidth: number;
  widthPx: number;
}) => {
  const targetCount = chartData.filter(isTargetColumn).length;
  const remainingPx = plotWidth - targetCount * widthPx;
  const otherWeightSum = chartData.reduce(
    (sum, point) => (isTargetColumn(point) ? sum : sum + point.slotHalfWidth * 2),
    0,
  );

  if (targetCount === 0 || remainingPx <= 0 || otherWeightSum <= 0) {
    return { chartData, domainMax, domainMin };
  }

  const targetWeight = (widthPx / remainingPx) * otherWeightSum;
  let cursor = domainMin;
  const layoutChartData = chartData.map((point) => {
    const weight = isTargetColumn(point) ? targetWeight : point.slotHalfWidth * 2;
    const slotHalfWidth = weight / 2;
    const timestamp = cursor + slotHalfWidth;
    cursor += weight;

    return { ...point, slotHalfWidth, timestamp };
  });

  return {
    chartData: layoutChartData,
    domainMax: cursor,
    domainMin,
  };
};
